import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import TuneIcon from "../Icons/tune-icon";
import styles from "./OrdersFiltersButton.module.css";

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "NOT_ASSIGNED", label: "Unassigned" },
  { value: "NOT_ACCEPTED_YET", label: "Not accepted yet" },
  { value: "STARTED", label: "Started" },
  { value: "PICKED_UP", label: "Picked up" },
  { value: "ON_THE_WAY", label: "On the way" },
];

const LEFT_COLUMN_OPTIONS = STATUS_FILTER_OPTIONS.slice(0, 4); // All, Scheduled, Unassigned, Not accepted yet
const RIGHT_COLUMN_OPTIONS = STATUS_FILTER_OPTIONS.slice(4, 7); // Started, Picked up, On the way

function CheckboxIcon({ checked }) {
  return (
    <span className={styles.checkboxWrap} data-state={checked ? "On" : "Off"} aria-hidden="true">
      {checked ? (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="18" height="18" rx="4" fill="#22C55E" stroke="#22C55E" strokeWidth="2" />
          <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="18" height="18" rx="4" stroke="#737373" strokeWidth="2" />
        </svg>
      )}
    </span>
  );
}

export default function OrdersFiltersButton({ statusFilters = [], setStatusFilters }) {
  const [open, setOpen] = useState(false);
  const [selectedStatusFilters, setSelectedStatusFilters] = useState(() => new Set(["all"]));
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left,
      minWidth: Math.max(rect.width, 200),
    });
  }, [open]);

  useEffect(() => {
    if (open) {
      setSelectedStatusFilters(
        statusFilters.length === 0 ? new Set(["all"]) : new Set(statusFilters)
      );
    }
  }, [open, statusFilters]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const insideTrigger = wrapperRef.current?.contains(e.target);
      const insideDropdown = dropdownRef.current?.contains(e.target);
      if (!insideTrigger && !insideDropdown) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTrigger = () => setOpen((o) => !o);

  const toggleStatus = (value) => {
    setSelectedStatusFilters((prev) => {
      const next = new Set(prev);
      if (value === "all") {
        if (next.has("all")) next.delete("all");
        else return new Set(["all"]);
      } else {
        next.delete("all");
        if (next.has(value)) next.delete(value);
        else next.add(value);
      }
      return next;
    });
  };

  const handleClear = () => {
    setSelectedStatusFilters(new Set(["all"]));
  };

  const handleApply = () => {
    const hasAll = selectedStatusFilters.has("all");
    const list = hasAll || selectedStatusFilters.size === 0
      ? []
      : Array.from(selectedStatusFilters).filter((v) => v !== "all");
    setStatusFilters?.(list);
    setOpen(false);
  };

  const renderOption = (option) => {
    const checked = option.value === "all"
      ? selectedStatusFilters.has("all") || selectedStatusFilters.size === 0
      : selectedStatusFilters.has(option.value);
    return (
      <div
        key={option.value}
        role="checkbox"
        tabIndex={0}
        className={styles.statusOptionRow}
        onClick={() => toggleStatus(option.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleStatus(option.value);
          }
        }}
        aria-checked={checked}
        aria-label={option.label}
      >
        <div className={styles.statusOptionCheckbox}>
          <CheckboxIcon checked={checked} />
        </div>
        <span className={styles.statusOptionLabel}>{option.label}</span>
      </div>
    );
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={handleTrigger}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Order filters"
        data-open={open}
      >
        <span className={styles.iconWrap} aria-hidden="true">
          <TuneIcon />
        </span>
      </button>
      {open &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.dropdown}
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: 440,
              minWidth: dropdownPosition.minWidth,
            }}
          >
            <div className={styles.dropdownBody}>
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderTitle}>Filter by status</span>
                </div>
                <div className={styles.statusColumns}>
                  <div className={styles.statusColumn}>
                    {LEFT_COLUMN_OPTIONS.map(renderOption)}
                  </div>
                  <div className={styles.statusColumn}>
                    {RIGHT_COLUMN_OPTIONS.map(renderOption)}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.footer}>
              <button type="button" className={styles.footerClear} onClick={handleClear}>
                Clear
              </button>
              <button type="button" className={styles.footerApply} onClick={handleApply}>
                Apply
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
